import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, TableItem } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { format } from 'date-fns';
import { useComponentVisible } from 'hooks';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiArrowsCounterClockwise, PiUserPlus } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import {
  useAccountRefreshVersion,
  useGetAccountListInfinite,
} from 'services/accounts';
import {
  IAccountFilteredListParams,
  IAccountResponse,
} from 'services/accounts/types';
import { STATUS } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { getNodes } from 'utils/table/nodes.utils';

enum ACCOUNT_COLUMNS {
  accountName,
  apiEndpoint,
  webEndpoint,
  apiVersion,
  webVersion,
  lastCheckedAt,
}

const accountsSearchInitialValues: IAccountFilteredListParams = {
  name: '',
};

const Accounts = () => {
  const [filters, setFilters] = useState<IAccountFilteredListParams>(
    accountsSearchInitialValues,
  );

  const { data, isLoading, fetchNextPage, isFetchingNextPage, refetch } =
    useGetAccountListInfinite({ ...filters, itemCount: 25 });

  const tableData = getNodes<IAccountResponse>(data?.pages.flat());

  const { t } = useTranslation();
  const navigate = useNavigate();
  const i18nextPrefix = 'account';
  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const columnsVisibility = useTableColumnsVisibility(
    'Accounts3',
    ACCOUNT_COLUMNS,
  );

  const onSelectChange = (action: Action, state: State) => {
    navigate(`${state.id ?? state.ids[state.ids.length - 1]}/finances`, {
      relative: 'path',
    });
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const refreshVersion = useAccountRefreshVersion();

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          path=''
          customBackItem={<div />}
          topbarContent={
            <>
              <div className='flex'>
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
              </div>

              <Button
                variant={BUTTON_VARIANT.submit}
                onClick={() => navigate('create', { replace: false })}>
                <Button.Text
                  text={t('button.create-what', { what: t('account.what') })}
                />
                <Button.Icon icon={<PiUserPlus />} />
              </Button>
            </>
          }>
          <QueryFilter<IAccountFilteredListParams>
            i18nextPrefix={i18nextPrefix}
            initialValues={accountsSearchInitialValues}>
            <>
              <QueryFilter.Form<IAccountFilteredListParams>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values => setFilters(values)}>
                <QueryFilter.InputsGrid>
                  <FormInput.Text
                    name='name'
                    label={t('input.label.accountName')}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isComponentVisible}
                customLabel={data =>
                  data.key === 'status'
                    ? t(`enum.STATUS.${STATUS[data.value as STATUS]}`)
                    : `${data.value}`
                }
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        id={columnsVisibility.tableId}
        select={select}
        columns={[
          {
            label: `${t(
              `${i18nextPrefix}.table.${
                ACCOUNT_COLUMNS[ACCOUNT_COLUMNS.accountName]
              }`,
            )}`,
            renderCell: item => (
              <TableItem>
                <div className='flex flex-row items-center gap-4'>
                  <TableItem.Text text={item.accountName} />
                </div>
              </TableItem>
            ),
            resize: true,
          },
          {
            label: 'API version',
            renderCell: item => (
              <TableItem>
                <TableItem.Text text={item.apiVersion} />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: 'WEB version',
            renderCell: item => (
              <TableItem>
                <TableItem.Text text={item.webVersion} />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: 'Last check version',
            renderCell: item => (
              <TableItem>
                <TableItem.Text
                  text={
                    item.lastCheckedAt
                      ? format(item.lastCheckedAt, 'HH:mm dd-MM-yyyy')
                      : undefined
                  }
                />
              </TableItem>
            ),
          },
          {
            label: '',
            renderCell: item => (
              <TableItem justifyPosition='end'>
                <div className='flex gap-2 items-center'>
                  <div className='flex flex-col items-end gap-2'>
                    {/* {item.license?.availableTo &&
                      new Date().getTime() >
                        new Date(item.license?.availableTo).getTime() && (
                        <Label color='red' size='compact'>
                          <PiCalendarSlash /> License expired
                        </Label>
                      )}

                    {item.license?.isDemo && item.license?.demoMode && (
                      <Label color='blue' withText size='compact'>
                        <PiTestTube /> DEMO ({DEMO_MODE[item.license.demoMode.id]})
                      </Label>
                    )} */}
                  </div>
                  <Button
                    onlyIcon
                    variant={BUTTON_VARIANT.warning}
                    isClear
                    onClick={async () =>
                      await refreshVersion.mutateAsync(item.id, {
                        onSuccess: () => refetch(),
                        onError: error => onErrorShowAllErrorsToast(error),
                      })
                    }>
                    <Button.Icon icon={<PiArrowsCounterClockwise />} />
                  </Button>
                </div>
              </TableItem>
            ),
          },
        ]}
        displayMode
        loading={isLoading || isFetchingNextPage}
        data={tableData}
        onFullyScroll={fetchNextPage}
      />
    </TopbarPage>
  );
};

export default Accounts;
